// 26차 후속 #82 Phase 1 — 포장 출고 스캔 정상/오류음.
//
// 정책:
//   - playOkBeep   : 짧고 높은 1-tone "띡"
//   - playErrorBeep: 길고 낮은 buzz "삐~~~"
// 브라우저 자동재생 제한 회피를 위해 AudioContext 는 사용자 입력 이벤트 안에서 lazy 생성.
// SSR (Next.js) 환경 안전을 위해 typeof window 가드.

type Ctor = typeof AudioContext;
type Win = Window &
  typeof globalThis & {
    AudioContext?: Ctor;
    webkitAudioContext?: Ctor;
  };

let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (ctx) return ctx;
  const w = window as Win;
  const C = w.AudioContext || w.webkitAudioContext;
  if (!C) return null;
  try {
    ctx = new C();
  } catch {
    ctx = null;
  }
  return ctx;
}

function tone(freq: number, durationMs: number, gain: number, type: OscillatorType = "sine"): void {
  const ac = getCtx();
  if (!ac) return;
  // 자동재생 정책으로 suspend 된 경우 resume 시도 (사용자 제스처 컨텍스트에서만 성공).
  if (ac.state === "suspended") {
    ac.resume().catch(() => {});
  }
  try {
    const osc = ac.createOscillator();
    const g = ac.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    // 클릭 잡음 방지를 위한 짧은 fade
    const now = ac.currentTime;
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(gain, now + 0.01);
    g.gain.setValueAtTime(gain, now + durationMs / 1000 - 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, now + durationMs / 1000);
    osc.connect(g).connect(ac.destination);
    osc.start(now);
    osc.stop(now + durationMs / 1000 + 0.02);
  } catch {
    // silent — 오디오 컨텍스트 실패 시 무시
  }
}

/** 정상 스캔 — 짧은 1-tone (예: 송장/상품 매칭, 전량 완료). */
export function playOkBeep(): void {
  tone(1000, 90, 0.18, "square");
}

/** 오류 스캔 — 길고 낮은 buzz "삐~~~" (예: 출고중지/포장완료/오스캔/송장없음). */
export function playErrorBeep(): void {
  tone(220, 450, 0.22, "sawtooth");
}

/**
 * 팀 채팅 새 메시지 알림 — 부드러운 2-tone "띵-동" (660Hz → 880Hz, 짧음).
 * 스캔 beep 보다 차분한 사인 톤으로 사무실 환경에서 거슬리지 않도록 설계.
 * AudioContext 는 scanBeep 과 공유 — 사용자 제스처 컨텍스트에서 첫 호출 시 unlock.
 */
export function playChatNotifyBeep(): void {
  tone(660, 110, 0.15, "sine");
  setTimeout(() => tone(880, 140, 0.15, "sine"), 110);
}
